'use client';

import cn from '@sindresorhus/class-names';
import Image from 'next/image';
import Link from 'next/link';
import {usePathname} from 'next/navigation';
import {useMemo, useState} from 'react';

import Check from '@ui-kit/src/assets/Icons/Check.svg';
import {Chevron} from '@ui-kit/src/assets/Icons/Chevron';
import {Globe} from '@ui-kit/src/assets/Icons/Globe';

import {useSwitchLanguageAnalytics} from '@web-landing/analytics/hooks/useSwitchLanguageAnalytics';

import styles from './SwitchLanguage.module.scss';

const LANGUAGES = [
  {lang: 'RU', label: 'Русский', itemName: 'ru'},
  {lang: 'EN', label: 'English', itemName: 'en'},
  {lang: '中文', label: '简体中文', itemName: 'zh'},
];

export const SwitchLanguage = ({isHeader = true}: {isHeader?: boolean}) => {
  const switchButtonLocation = isHeader ? 'header' : 'footer';
  const {buttonClick, dropdownListClick} = useSwitchLanguageAnalytics();
  const router = usePathname() as string;
  const [locationsListShown, setLocationsListShown] = useState(false);

  const currentLocation = useMemo(() => {
    if (router.startsWith('/en')) return 'EN';
    if (router.startsWith('/zh')) return '中文';
    return 'RU';
  }, [router]);

  const toggleLocationsList = () => {
    buttonClick(switchButtonLocation);
    setLocationsListShown(!locationsListShown);
  };

  const selectLocation = (lang: string) => {
    dropdownListClick({
      locale: LANGUAGES.find((elem) => elem.lang === lang)?.itemName ?? 'ru',
      location: switchButtonLocation,
    });
    setLocationsListShown(false);
  };

  const generateButtonPath = (locale: string) => {
    const language = locale === 'ru' ? '' : locale;
    const slicedPart =
      router.startsWith('/en') || router.startsWith('/zh')
        ? router.slice(3)
        : router;

    const path = `/${language}${slicedPart}`.replace('//', '/');

    return path;
  };

  return (
    <div className={styles.container}>
      <button
        onClick={toggleLocationsList}
        className={cn(styles.button, {[styles.active]: locationsListShown})}
        data-testid="switch_language_button"
      >
        <Globe />
        <p className={cn(styles.text, {[styles.headerLocation]: isHeader})}>
          {currentLocation}
        </p>
        <Chevron
          className={cn(styles.chevron, {[styles.down]: locationsListShown})}
        />
      </button>
      {locationsListShown && (
        <>
          <div className={cn(styles.list, {[styles.headerPosition]: isHeader})}>
            {LANGUAGES.map(({lang, label, itemName}) => (
              <Link
                key={lang}
                className={styles.language}
                href={generateButtonPath(itemName)}
                data-testid={`switch_button_lang_${itemName}_id`}
                onClick={() => selectLocation(lang)}
              >
                <p className={styles.label}>{label}</p>
                {currentLocation === lang && (
                  <Image
                    src={Check}
                    alt="Current locale"
                    width={12}
                    height={12}
                  />
                )}
              </Link>
            ))}
          </div>
          <div
            className={styles.backgroundLayer}
            onClick={() => setLocationsListShown(false)}
          />
        </>
      )}
    </div>
  );
};
